import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Receipt, ShoppingCart, ChevronRight } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { paymentLabel } from "../../lib/payments";

export function DashboardRecentSales({ recentInvoices, primaryColor, devise, isDark }) {
  const navigate = useNavigate();
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.4 }} className="lg:col-span-2">
      <Card className="border-border/50">
        <CardContent className="p-3 md:p-5">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <h2 className="text-base md:text-lg font-semibold">Dernières ventes</h2>
            <Link to="/history">
              <Button variant="ghost" size="sm" className="text-sm group">
                Voir tout <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
          {recentInvoices.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border/50">
                    <th className="text-left text-xs font-medium text-muted-foreground py-3 px-2">Article</th>
                    <th className="text-left text-xs font-medium text-muted-foreground py-3 px-2 hidden md:table-cell">Taille</th>
                    <th className="text-right text-xs font-medium text-muted-foreground py-3 px-2">Montant</th>
                    <th className="text-center text-xs font-medium text-muted-foreground py-3 px-2 hidden md:table-cell">Paiement</th>
                    <th className="text-right text-xs font-medium text-muted-foreground py-3 px-2">Heure</th>
                  </tr>
                </thead>
                <tbody>
                  {recentInvoices.slice(0, 5).map((invoice) => (
                    <tr key={invoice.id} onClick={() => navigate(`/invoice/${invoice.id}`)}
                      className="border-b border-border/30 cursor-pointer transition-colors hover:bg-[#252525]"
                      data-testid={`transaction-${invoice.id}`}>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${primaryColor}15` }}>
                            <Receipt className="w-4 h-4" style={{ color: primaryColor }} />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate max-w-[150px]">
                              {invoice.items?.[0]?.product_name || invoice.invoice_number}
                            </p>
                            {invoice.items_count > 1 && (
                              <p className="text-xs text-muted-foreground">+{invoice.items_count - 1} autre{invoice.items_count > 2 ? 's' : ''}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-2 hidden md:table-cell">
                        <span className="text-sm text-muted-foreground">{invoice.items?.[0]?.size || "—"}</span>
                      </td>
                      <td className="py-3 px-2 text-right">
                        <span className="font-semibold" style={{ color: primaryColor }}>
                          {invoice.total.toLocaleString('fr-FR')} {devise}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-center hidden md:table-cell">
                        <Badge variant="secondary" className={`text-xs ${
                          invoice.payment_method === 'cash' ? 'bg-gray-500/20 text-gray-400' :
                          invoice.payment_method === 'mobile' ? 'bg-green-500/20 text-green-400' :
                          invoice.payment_method === 'card' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-purple-500/20 text-purple-400'
                        }`}>
                          {paymentLabel(invoice)}
                        </Badge>
                      </td>
                      <td className="py-3 px-2 text-right">
                        <span className="text-sm text-muted-foreground">
                          {format(new Date(invoice.created_at), "HH:mm", { locale: fr })}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="relative w-24 h-24 mx-auto mb-4">
                <div className="absolute inset-0 rounded-full" style={{ backgroundColor: `${primaryColor}10` }} />
                <div className="absolute inset-2 rounded-full flex items-center justify-center" style={{ backgroundColor: `${primaryColor}15` }}>
                  <ShoppingCart className="w-10 h-10" style={{ color: primaryColor, opacity: 0.7 }} />
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full" style={{ backgroundColor: `${primaryColor}30` }} />
                <div className="absolute -bottom-2 left-0 w-3 h-3 rounded-full" style={{ backgroundColor: `${primaryColor}20` }} />
              </div>
              <p className="font-medium mb-1">Aucune vente encore.</p>
              <p className="text-sm text-muted-foreground mb-4">Ouvrez la caisse pour commencer !</p>
              <Link to="/admin/sales">
                <button data-testid="goto-caisse-btn"
                  style={{ backgroundColor: '#FF4500', color: '#FFFFFF', border: 'none', borderRadius: 10,
                    padding: '10px 20px', fontWeight: 600, fontSize: 13, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 4, transition: 'background 0.2s ease-out' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#E03D00'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = '#FF4500'}>
                  Aller à la caisse <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}
